import React, {useState, useEffect} from 'react';
import Cookies from 'lib/cookies';
import {jwtDecode as _decode} from 'lib/jwtDecode';

import {getEnvironment, isInternal} from '@albert-io/environment';
import {Banner, Button, Dialogue, Text} from '@albert-io/atomic';
import {upperFirst} from 'lib/stringUtils';

import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import type {DecodedLaunchContextCookie} from './ImportRoster.react';

interface Props extends PropsWithClassNameRequired {
  handleClose: () => void;
  classroomId: string;
}

const ImportRosterSuccess = ({className, handleClose, classroomId}: Props) => {
  // Derive the LMS name from the launch-context cookie once, at init — it's read synchronously
  // (client-only, no SSR) and never changes, so no mount effect is needed. Falls back to 'LMS' if
  // the cookie is missing/undecodable, matching the prior behavior (effect error left the default).
  const [lmsName] = useState(() => {
    try {
      const launchContextCookie = Cookies.get('lti_launch_context');
      const launchContextDecoded = _decode<DecodedLaunchContextCookie>(
        launchContextCookie as string
      );
      return upperFirst(launchContextDecoded.tool_platform_product_family_code || 'LMS');
    } catch {
      return 'LMS';
    }
  });

  useEffect(() => {
    const classroomStudentsResource = resource('classrooms_v1')
      .mandarkEndpoint(['classrooms_v1', classroomId, 'students_v2'])
      .done();
    invalidatePartialInterest(classroomStudentsResource);

    const classroomInvitationResource = resource('classroom_invitations_v1')
      .mandarkEndpoint(['classroom_invitations_v1'])
      .done();
    invalidatePartialInterest(classroomInvitationResource);

    let cookieDomain = 'albert.io';
    if (isInternal()) {
      cookieDomain = getEnvironment();
    }

    Cookies.remove('lti_launch_context', {domain: cookieDomain});
  }, [classroomId]);

  return (
    <Dialogue hideCloseBtn className={className} size='m' title='Roster successfully imported.'>
      <Dialogue.Body>
        <div className='flex flex-col'>
          <Text>Your roster was imported successfully!</Text>

          <Banner className='mt-x3 mx-auto flex justify-center'>
            <Banner.Icon icon='info-circle' iconStyle='regular' className='self-center' />
            <Text>
              If you intend to import more classes from {lmsName}, you’ll need to launch into Albert
              from each of the courses you’d like to import.
            </Text>
          </Banner>
        </div>
      </Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button onClick={() => handleClose()}>Dismiss</Button>
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};

export default ImportRosterSuccess;
